import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog'
import { type ComponentProps, type ReactElement } from 'react'
import { cn } from '@/lib/utils'

function AlertDialog (props: ComponentProps<typeof AlertDialogPrimitive.Root>): ReactElement {
  return <AlertDialogPrimitive.Root {...props} />
}

function AlertDialogContent ({ className, children, ...props }: ComponentProps<typeof AlertDialogPrimitive.Content>): ReactElement {
  return (
    <AlertDialogPrimitive.Portal>
      <AlertDialogPrimitive.Overlay className='fixed inset-0 z-1000 bg-black/45' />
      <AlertDialogPrimitive.Content
        className={cn(
          'fixed top-1/2 left-1/2 z-1000 w-full max-w-105 -translate-x-1/2 -translate-y-1/2 m-5 rounded-[10px] border border-(--bp-border) bg-(--bp-surface) shadow-(--shadow-lg) outline-none',
          className
        )}
        {...props}
      >
        {children}
      </AlertDialogPrimitive.Content>
    </AlertDialogPrimitive.Portal>
  )
}

function AlertDialogHeader ({ className, ...props }: ComponentProps<'div'>): ReactElement {
  return <div className={cn('p-6', className)} {...props} />
}

function AlertDialogTitle ({ className, ...props }: ComponentProps<typeof AlertDialogPrimitive.Title>): ReactElement {
  return (
    <AlertDialogPrimitive.Title
      className={cn('text-[16px] font-bold text-(--bp-ink) mb-2', className)}
      {...props}
    />
  )
}

function AlertDialogDescription ({ className, ...props }: ComponentProps<typeof AlertDialogPrimitive.Description>): ReactElement {
  return (
    <AlertDialogPrimitive.Description
      className={cn('text-[13px] text-muted-foreground leading-relaxed', className)}
      {...props}
    />
  )
}

function AlertDialogFooter ({ className, ...props }: ComponentProps<'div'>): ReactElement {
  return (
    <div
      className={cn(
        'flex justify-end gap-2 px-6 py-4 bg-(--bp-surface2) border-t border-(--bp-border) rounded-b-[10px]',
        className
      )}
      {...props}
    />
  )
}

function AlertDialogCancel ({ className, ...props }: ComponentProps<typeof AlertDialogPrimitive.Cancel>): ReactElement {
  return (
    <AlertDialogPrimitive.Cancel
      className={cn(
        'inline-flex items-center gap-1.5 px-3.5 py-1.75 text-[13px] font-medium rounded-md cursor-pointer transition-all whitespace-nowrap bg-white text-muted-foreground border border-(--bp-border-s) hover:bg-(--bp-surface2) disabled:opacity-50',
        className
      )}
      {...props}
    />
  )
}

function AlertDialogAction ({ className, ...props }: ComponentProps<typeof AlertDialogPrimitive.Action>): ReactElement {
  return (
    <AlertDialogPrimitive.Action
      className={cn(
        'inline-flex items-center gap-1.5 px-3.5 py-1.75 text-[13px] font-medium rounded-md cursor-pointer transition-all whitespace-nowrap bg-white text-destructive border border-(--bp-critical-border) hover:bg-(--bp-critical-pale) disabled:opacity-50',
        className
      )}
      {...props}
    />
  )
}

export {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction
}
